import React, { useEffect, useState } from 'react';
import { Card, Collapse, Empty, Spin, Table, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import api from '../../api';

/**
 * The agent To Do cards rolled up: what is outstanding on the campaign (for
 * records keyed on a campaign) or on the team (those keyed on a team). Each
 * card shows its count; its rows load only when it is expanded, since a
 * campaign can have a great many. Cards the user has no access to are not
 * returned by the server. Not tied to the page's date window: anything
 * outstanding counts, over the same look-back as an agent's To Do.
 */

// Where a row opens, and what its date / person / detail columns mean.
const KIND = {
  case:     { link: (id) => `/cases/${id}`,                date: 'Call Date',     who: 'Assigned To', detail: 'Form' },
  error:    { link: (id) => `/errors/${id}`,               date: 'Created',       who: 'Assigned To', detail: 'Form' },
  coaching: { link: (id) => `/coaching/sessions/${id}`,    date: 'Due Date',      who: 'Coach',       detail: 'Title' },
  incident: { link: (id) => `/compliance/incidents/${id}`, date: 'Incident Date', who: 'Assigned To', detail: '' },
  variance: { link: (id) => `/schedule-variances/${id}`,   date: 'Variance Date', who: 'Reporter',    detail: 'Type' },
  helpdesk: { link: (id) => `/helpdesk/${id}`,             date: 'Created',       who: 'Assigned To', detail: 'Subject' },
};

const KEY_KIND = {
  evals_ack: 'case', feedback_ack: 'case', evals_complete: 'case', feedback_complete: 'case',
  errors_ack: 'error', errors_complete: 'error',
  coaching_complete: 'coaching', coaching_execute: 'coaching',
  compliance_ack: 'incident', compliance_complete: 'incident',
  variances_open: 'variance', helpdesk_open: 'helpdesk',
};

function CardRows({ cardKey, params }) {
  const navigate = useNavigate();
  const [rows, setRows]       = useState(null);
  const kind = KIND[KEY_KIND[cardKey]] || KIND.case;

  useEffect(() => {
    setRows(null);
    api.get(`/api/campaign-summary/todo/${cardKey}`, { params })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]));
  }, [cardKey, params.campaign_id, params.team_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (rows == null) return <div style={{ textAlign: 'center', padding: 16 }}><Spin /></div>;
  const strSort = (k) => (a, b) => (a[k] ?? '').localeCompare(b[k] ?? '');
  return (
    <Table
      rowKey="ID"
      size="small"
      dataSource={rows}
      pagination={{ defaultPageSize: 10, size: 'small', hideOnSinglePage: true }}
      scroll={{ x: 'max-content' }}
      onRow={(r) => ({ onClick: () => navigate(kind.link(r.ID)), style: { cursor: 'pointer' } })}
      columns={[
        { title: cardKey === 'coaching_execute' ? 'Participants' : 'Agent', dataIndex: 'AgentName', key: 'AgentName',
          sorter: strSort('AgentName'), render: (v) => v || '—' },
        { title: kind.date, dataIndex: 'RecordDate', key: 'RecordDate', width: 120, sorter: strSort('RecordDate'),
          render: (v) => v || '—' },
        { title: 'Status', dataIndex: 'Status', key: 'Status', width: 150, sorter: strSort('Status'),
          render: (v) => (v ? <Tag style={{ margin: 0 }}>{v}</Tag> : '—') },
        { title: kind.who, dataIndex: 'AssignedToName', key: 'AssignedToName', sorter: strSort('AssignedToName'),
          render: (v) => v || '—' },
        ...(kind.detail ? [{ title: kind.detail, dataIndex: 'Detail', key: 'Detail', render: (v) => v || '—' }] : []),
      ]}
    />
  );
}

export default function TodoTab({ campaignId, campaign, teamId, team }) {
  const [cards, setCards]     = useState([]);
  const [loading, setLoading] = useState(false);
  const params = { campaign_id: campaignId || undefined, team_id: teamId || undefined };

  useEffect(() => {
    if (!campaignId && !teamId) { setCards([]); return; }
    setLoading(true);
    api.get('/api/campaign-summary/todo', { params })
      .then((r) => setCards(r.data || []))
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
  }, [campaignId, teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!campaignId && !teamId) {
    return <Empty description="Select a campaign" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 40 }} />;
  }

  // As on an agent's To Do: cards with something outstanding first.
  const group = (scope) => {
    const list = cards.filter((c) => c.scope === scope);
    return [...list.filter((c) => c.count > 0), ...list.filter((c) => !(c.count > 0))];
  };
  const section = (scope, heading) => {
    const list = group(scope);
    if (!list.length) return null;
    return (
      <Card size="small" title={heading} style={{ marginBottom: 16 }}>
        <Collapse
          size="small"
          destroyInactivePanel
          items={list.map((c) => ({
            key: c.key,
            collapsible: c.count > 0 ? undefined : 'disabled',
            showArrow: c.count > 0,
            label: (
              <span>
                <Typography.Text strong>{c.title}</Typography.Text>
                <Tag color={c.count > 0 ? 'red' : 'green'} style={{ marginLeft: 8, fontWeight: 600 }}>
                  {c.count == null ? '—' : c.count > 0 ? c.count : '✓'}
                </Tag>
              </span>
            ),
            children: <CardRows cardKey={c.key} params={params} />,
          }))}
        />
      </Card>
    );
  };

  return (
    <Spin spinning={loading}>
      {section('campaign', `Campaign — ${campaign?.Name || ''}`)}
      {section('team', `Team — ${team?.Name || ''}`)}
    </Spin>
  );
}
